import React, { useState } from 'react';
import { string, object, shape } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import css from './ReviewResponseForm.module.css';

const MAX_LENGTH = 1000;

/**
 * ReviewResponseForm — allows providers to add a public response to a review.
 *
 * Shows any existing response and allows editing/deleting it.
 *
 * Usage (in ProfilePage or review list, for the review author's listing provider):
 *   {isProvider && (
 *     <ReviewResponseForm review={review} />
 *   )}
 *
 * @param {Object} props.review — Sharetribe review entity
 * @param {Object} [props.currentUser]
 * @param {string} [props.className]
 */
const ReviewResponseForm = ({ review, className }) => {
  const reviewId = review?.id?.uuid;
  const existing = review?.attributes?.metadata?.providerResponse;

  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(existing?.text || '');
  const [isLoading, setIsLoading] = useState(false);
  const [saved, setSaved] = useState(!!existing);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!text.trim() || isLoading) return;
    setIsLoading(true);
    try {
      await fetch(`/api/reviews/${reviewId}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      setSaved(true);
      setIsEditing(false);
    } catch (err) {
      console.error('[ReviewResponseForm] error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Remove your response?')) return;
    setIsLoading(true);
    try {
      await fetch(`/api/reviews/${reviewId}/respond`, { method: 'DELETE' });
      setText('');
      setSaved(false);
      setIsEditing(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={classNames(css.root, className)}>
      {saved && !isEditing ? (
        <div className={css.response}>
          <p className={css.responseLabel}>
            <FormattedMessage id="ReviewResponseForm.responseLabel" />
          </p>
          <p className={css.responseText}>{existing?.text || text}</p>
          <div className={css.responseActions}>
            <button type="button" onClick={() => setIsEditing(true)} className={css.editBtn}>
              <FormattedMessage id="ReviewResponseForm.edit" />
            </button>
            <button type="button" onClick={handleDelete} className={css.deleteBtn} disabled={isLoading}>
              <FormattedMessage id="ReviewResponseForm.delete" />
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className={css.form}>
          {!saved && !isEditing && (
            <button type="button" onClick={() => setIsEditing(true)} className={css.addBtn}>
              + <FormattedMessage id="ReviewResponseForm.addResponse" />
            </button>
          )}
          {isEditing && (
            <>
              <textarea
                value={text}
                onChange={e => setText(e.target.value)}
                maxLength={MAX_LENGTH}
                rows={4}
                className={css.textarea}
                placeholder="Write your response..."
                autoFocus
              />
              <p className={css.charCount}>{text.length}/{MAX_LENGTH}</p>
              <div className={css.formActions}>
                <button type="submit" disabled={!text.trim() || isLoading} className={css.submitBtn}>
                  <FormattedMessage id="ReviewResponseForm.submit" />
                </button>
                <button type="button" onClick={() => setIsEditing(false)} className={css.cancelBtn}>
                  <FormattedMessage id="ReviewResponseForm.cancel" />
                </button>
              </div>
            </>
          )}
        </form>
      )}
    </div>
  );
};

ReviewResponseForm.defaultProps = { className: null };
ReviewResponseForm.propTypes = { review: object.isRequired, className: string };

export default ReviewResponseForm;
